import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchProductById } from "../../services/api";
import { useCart } from "../../context/CartContext";

export default function ProductView() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [product, setProduct] = useState(null);
    const [mediaIdx, setMediaIdx] = useState(0);

    const { addToCart, isInCart } = useCart();

    useEffect(() => {
        fetchProductById(id)
            .then(res => setProduct(res.data))
            .catch(console.error);
    }, [id]);

    if (!product) return <div className="p-8">Loading...</div>;

    const mainMedia = product.media?.[mediaIdx];
    const alreadyInCart = isInCart(product.id); // ✅ check cart

    const handleAddToCart = () => {
        const fallbackThumbnail = product.media?.find(m => m.type === "image")?.url || null;

        addToCart({
            id: product.id,
            name: product.name,
            price: product.price,
            thumbnail: product.thumbnail || fallbackThumbnail,
            quantity: 1,
        });
    };

    const handleGoToCart = () => {
        navigate("/cart");
    };

    return (
        <div className="max-w-5xl mx-auto mt-8 flex flex-col md:flex-row gap-8">
            {/* Gallery */}
            <div className="flex flex-col items-center w-full md:w-1/2">
                <div className="w-full bg-white rounded-lg shadow flex items-center justify-center h-96 mb-3">
                    {mainMedia ? (
                        mainMedia.type === "image" ? (
                            <img
                                src={mainMedia.url}
                                alt={product.name}
                                className="max-h-96 max-w-full object-contain"
                            />
                        ) : (
                            <video controls className="max-h-96 max-w-full object-contain">
                                <source src={mainMedia.url} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        )
                    ) : (
                        <div className="text-gray-400">No media</div>
                    )}
                </div>

                {/* Thumbnails */}
                <div className="flex gap-2 mt-2">
                    {product.media?.map((m, idx) => (
                        <div
                            key={m.id}
                            className={`border-2 rounded cursor-pointer p-1 ${
                                idx === mediaIdx ? "border-orange-600" : "border-transparent"
                            }`}
                            onClick={() => setMediaIdx(idx)}
                        >
                            {m.type === "image" ? (
                                <img src={m.url} alt="thumb" className="h-16 w-16 object-cover rounded" />
                            ) : (
                                <video className="h-16 w-16 object-cover rounded">
                                    <source src={m.url} />
                                </video>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Product Info */}
            <div className="flex-1">
                <div className="font-bold text-2xl mb-1">{product.name}</div>
                <div className="text-gray-600 mb-4">{product.description}</div>
                <div className="flex items-center text-yellow-500 text-lg mb-1">
                    {product.ratings.length > 0 ? (
                        <>
                            <span className="mr-1">
                                {(
                                    product.ratings.reduce((sum, r) => sum + r.stars, 0) /
                                    product.ratings.length
                                ).toFixed(1)}
                            </span>
                            ★ ({product.ratings.length} reviews)
                        </>
                    ) : (
                        "No rating"
                    )}
                </div>
                <div className="font-bold text-3xl text-orange-600 mb-4">₹{product.price}</div>

                {/* ✅ Dynamic Button */}
                {alreadyInCart ? (
                    <button
                        onClick={handleGoToCart}
                        className="bg-green-600 text-white px-6 py-3 rounded font-semibold hover:bg-green-700 transition"
                    >
                        Go to Cart
                    </button>
                ) : (
                    <button
                        onClick={handleAddToCart}
                        className="bg-orange-500 text-white px-6 py-3 rounded font-semibold hover:bg-orange-600 transition"
                    >
                        Add to Cart
                    </button>
                )}
            </div>
        </div>
    );
}
